import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from 'sonner';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import DayPlanner from './components/DayPlanner';
import BirthdaySplash from './components/BirthdaySplash';
import LoginPage from './pages/LoginPage';
import Overview from './pages/Overview';
import ManageOffices from './pages/ManageOffices';
import ManageUsers from './pages/ManageUsers';
import SystemConfig from './pages/SystemConfig';
import Employees from './pages/Employees';
import Recruitment from './pages/Recruitment';
import Onboarding from './pages/Onboarding';
import Attendance from './pages/Attendance';
import Performance from './pages/Performance';
import Payroll from './pages/Payroll';
import MySalary from './pages/MySalary';
import LeaveManagement from './pages/LeaveManagement';
import ContractLedger from './pages/ContractLedger';
import LettersLedger from './pages/LettersLedger';
import AllDocuments from './pages/AllDocuments';
import CompanyCredentials from './pages/CompanyCredentials';
import Invoices from './pages/Invoices';
import Tasks from './pages/Tasks';
import Schedule from './pages/Schedule';
import Team from './pages/Team';
import MyTeam from './pages/MyTeam';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import MyLeave from './pages/MyLeave';
import TeamLeave from './pages/TeamLeave';
import MyTasks from './pages/employee/MyTasks';
import MyCalendar from './pages/employee/MyCalendar';
import MyProfile from './pages/employee/MyProfile';
import MyExpenses from './pages/employee/MyExpenses';
import ExpenseManagement from './pages/ExpenseManagement';
import PolicyManagement from './pages/PolicyManagement';
import CompanyPolicies from './pages/CompanyPolicies';
import TeamCalendar from './pages/TeamCalendar';
import MyTimesheet from './pages/employee/MyTimesheet';
import TimesheetReview from './pages/TimesheetReview';
import TimesheetCategories from './pages/TimesheetCategories';
import NewsManagement from './pages/NewsManagement';
import Newsroom from './pages/Newsroom';
import AdminDataManagement from './pages/AdminDataManagement';
import AuditTrail from './pages/AuditTrail';
import Ping from './pages/Ping';
import VerifyEmployee from './pages/VerifyEmployee';
import EmailConfiguration from './pages/EmailConfiguration';
import QRScanLogs from './pages/QRScanLogs';
import PlatformAnalytics from './pages/PlatformAnalytics';
import EcoCoins from './pages/EcoCoins';
import MyCoins from './pages/employee/MyCoins';
import Surveys from './pages/Surveys';
import ExpenseCategories from './pages/ExpenseCategories';
import ExpenseSettings from './pages/ExpenseSettings';
import Leads from './pages/Leads';
import MeetingPad from './pages/MeetingPad';
import Documentation from './pages/Documentation';
import SurveyPopup from './components/SurveyPopup';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="loading-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return isAuthenticated() ? children : <Navigate to="/login" replace />;
};

// Check-In Gate — blocks app until user checks in, then shows Day Planner
const CheckInGate = ({ children }) => {
  const { getAuthHeaders, isSuperAdmin, user } = useAuth();
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);
  const [showPlanner, setShowPlanner] = useState(false);
  const [isReplan, setIsReplan] = useState(false);
  const [showBirthday, setShowBirthday] = useState(false);
  const [birthdayChecked, setBirthdayChecked] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchStatus = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/api/attendance/my-status`, { headers });
      if (res.ok) setStatus(await res.json());
    } catch (e) { console.error(e); }
    finally { setChecking(false); }
  }, [getAuthHeaders, API_URL]);

  // Check for birthdays on every login/load (all users including super admin)
  useEffect(() => {
    if (!birthdayChecked) {
      setBirthdayChecked(true);
      setShowBirthday(true);
    }
  }, [birthdayChecked]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Listen for re-plan event from DashboardDigest
  useEffect(() => {
    const handler = () => { setIsReplan(true); setShowPlanner(true); };
    window.addEventListener('replan-day', handler);
    return () => window.removeEventListener('replan-day', handler);
  }, []);

  const doCheckIn = async () => {
    setCheckingIn(true);
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/api/attendance/check-in`, {
        method: 'POST', headers, body: JSON.stringify({})
      });
      if (res.ok) {
        await fetchStatus();
        // Notify dashboard and other components to refresh after check-in
        window.dispatchEvent(new Event('checkin-complete'));
        setShowPlanner(true);
      }
      else {
        const err = await res.json();
        alert(err.detail || 'Failed to check in');
      }
    } catch (e) { console.error(e); }
    finally { setCheckingIn(false); }
  };

  // Birthday splash for all users (renders on top of everything)
  const birthdayOverlay = showBirthday ? (
    <BirthdaySplash onClose={() => setShowBirthday(false)} />
  ) : null;

  // Super admins bypass the check-in gate but still see birthdays
  if (isSuperAdmin() || checking) return <>{children}{birthdayOverlay}</>;

  // Non-working day (e.g., weekend per office config) — bypass the gate
  if (status && status.is_working_day === false) {
    return (
      <>
        {children}
        {birthdayOverlay}
        {!showBirthday && <SurveyPopup />}
      </>
    );
  }

  // Already checked in — pass through with birthday + planner + survey
  if (status?.has_checked_in) {
    return (
      <>
        {children}
        {birthdayOverlay}
        {showPlanner && !showBirthday && (
          <DayPlanner
            userName={user?.first_name ? `${user.first_name} ${user.last_name}` : ''}
            onClose={() => { setShowPlanner(false); setIsReplan(false); window.dispatchEvent(new Event('checkin-complete')); }}
            isReplan={isReplan}
          />
        )}
        {!showPlanner && !showBirthday && <SurveyPopup />}
      </>
    );
  }

  // Use backend-calculated early/late based on office local time and configured work start
  const diff = status?.early_late_minutes ?? 0;
  const absDiff = Math.abs(diff);
  const h = Math.floor(absDiff / 60);
  const m = absDiff % 60;
  const timeLabel = diff > 0
    ? `You are ${h > 0 ? `${h}h ` : ''}${m}m late`
    : diff < 0
      ? `You are ${h > 0 ? `${h}h ` : ''}${m}m early`
      : "You're right on time";

  return (
    <>
      {children}
      <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center" data-testid="checkin-gate">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-100 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">Check In Required</h2>
          <p className={`text-sm font-semibold mb-3 ${diff > 0 ? 'text-red-600' : 'text-green-600'}`}>{timeLabel}</p>
          <p className="text-sm text-gray-600 mb-6 leading-relaxed">
            Your daily check-in is how your line manager and HR manage your attendance. 
            Please check in to continue using the system.
          </p>
          <button
            onClick={doCheckIn}
            disabled={checkingIn}
            className="w-full py-3 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold transition-colors disabled:opacity-50"
            data-testid="gate-checkin-button"
          >
            {checkingIn ? 'Checking in...' : 'Check In Now'}
          </button>
        </div>
      </div>
    </>
  );
};

// Main App Layout Component
const AppLayout = () => {
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') || 'light';
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  const { user, currentOffice, isEmployee } = useAuth();

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Auto-collapse sidebar on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setSidebarCollapsed(true);
      } else {
        setSidebarCollapsed(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  return (
    <CheckInGate>
    <div className="flex h-screen overflow-hidden bg-background" data-testid="app-layout">
      <Sidebar 
        collapsed={sidebarCollapsed} 
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar 
          theme={theme} 
          onThemeToggle={toggleTheme}
          currentUser={user}
          currentOffice={currentOffice}
        />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="max-w-[1440px] mx-auto">
            <Routes>
              <Route path="/" element={<Navigate to="/overview" replace />} />
              <Route path="/overview" element={<Overview />} />
              <Route path="/manage-offices" element={<ManageOffices />} />
              <Route path="/manage-users" element={<ManageUsers />} />
              <Route path="/system-config" element={<SystemConfig />} />
              <Route path="/employees" element={<Employees />} />
              <Route path="/recruitment" element={<Recruitment />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/attendance" element={<Attendance />} />
              <Route path="/performance" element={<Performance />} />
              <Route path="/payroll" element={<Payroll />} />
              <Route path="/my-salary" element={<MySalary />} />
              <Route path="/leave-management" element={<LeaveManagement />} />
              <Route path="/contracts" element={<ContractLedger />} />
              <Route path="/letters" element={<LettersLedger />} />
              <Route path="/documents-ledger" element={<AllDocuments />} />
              <Route path="/company-credentials" element={<CompanyCredentials />} />
              <Route path="/invoices" element={<Invoices />} />
              <Route path="/profile" element={isEmployee() ? <MyProfile /> : <Profile />} />
              <Route path="/my-leave" element={<MyLeave />} />
              <Route path="/team-leave" element={<TeamLeave />} />
              <Route path="/my-tasks" element={<MyTasks />} />
              <Route path="/my-calendar" element={<MyCalendar />} />
              <Route path="/my-expenses" element={<MyExpenses />} />
              <Route path="/expense-management" element={<ExpenseManagement />} />
              <Route path="/tasks" element={<Tasks />} />
              <Route path="/schedule" element={<Schedule />} />
              <Route path="/team" element={<Team />} />
              <Route path="/my-team" element={<MyTeam />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/policy-management" element={<PolicyManagement />} />
              <Route path="/company-policies" element={<CompanyPolicies />} />
              <Route path="/team-calendar" element={<TeamCalendar />} />
              <Route path="/my-timesheet" element={<MyTimesheet />} />
              <Route path="/timesheet-review" element={<TimesheetReview />} />
              <Route path="/timesheet-categories" element={<TimesheetCategories />} />
              <Route path="/newsroom" element={<Newsroom />} />
              <Route path="/news-management" element={<NewsManagement />} />
              <Route path="/admin/data-management" element={<AdminDataManagement />} />
              <Route path="/admin/email-config" element={<EmailConfiguration />} />
              <Route path="/admin/qr-scan-logs" element={<QRScanLogs />} />
              <Route path="/platform-analytics" element={<PlatformAnalytics />} />
              <Route path="/ecocoins" element={<EcoCoins />} />
              <Route path="/my-coins" element={<MyCoins />} />
              <Route path="/surveys" element={<Surveys />} />
              <Route path="/admin/expense-categories" element={<ExpenseCategories />} />
              <Route path="/admin/expense-settings" element={<ExpenseSettings />} />
              <Route path="/audit-trail" element={<AuditTrail />} />
              <Route path="/ping" element={<Ping />} />
              <Route path="/leads" element={<Leads />} />
              <Route path="/meeting-pad" element={<MeetingPad />} />
              <Route path="/documentation" element={<Documentation />} />
              <Route path="*" element={<Navigate to="/overview" replace />} />
            </Routes>
          </div>
        </main>
      </div>
    </div>
    </CheckInGate>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/verify" element={<VerifyEmployee />} />
          <Route 
            path="/*" 
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            } 
          />
        </Routes>
        <Toaster 
          position="top-right"
          closeButton
          toastOptions={{
            style: {
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
            },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
